import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';

const DEFAULT_AVAILABILITY_FILTER = 'filter.v.availability';
const DEFAULT_AVAILABILITY_VALUE = '1';

function getDefaultAvailabilityURL() {
  const url = new URL(window.location.href);
  const availabilityValues = url.searchParams.getAll(DEFAULT_AVAILABILITY_FILTER);

  if (availabilityValues.length === 1 && availabilityValues[0] === DEFAULT_AVAILABILITY_VALUE) return;

  url.searchParams.delete(DEFAULT_AVAILABILITY_FILTER);
  url.searchParams.set(DEFAULT_AVAILABILITY_FILTER, DEFAULT_AVAILABILITY_VALUE);

  return url;
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    const defaultAvailabilityURL = getDefaultAvailabilityURL();
    if (defaultAvailabilityURL) {
      this.#renderDefaultAvailabilityFilter(defaultAvailabilityURL);
      return;
    }

    this.#initializeResultsList();
  }

  #initializeResultsList() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  /**
   * Render the collection from Shopify's native availability-filtered URL without a full page reload.
   * @param {URL} url
   */
  async #renderDefaultAvailabilityFilter(url) {
    history.replaceState({ urlParameters: url.searchParams.toString() }, '', url.toString());

    try {
      await sectionRenderer.renderSection(this.sectionId, { cache: false, url });
    } catch (error) {
      console.error(error);
      this.#initializeResultsList();
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
